import { mysteries, mysteryClues } from "@/mocks/home";
import SectionHead from "./SectionHead";

const stateTone: Record<string, string> = {
  "待新证": "text-accent-300 border-accent-500/30 bg-accent-500/10",
  "档案最全": "text-secondary-300 border-secondary-500/30 bg-secondary-500/10",
  "交叉核实中": "text-primary-300 border-primary-500/30 bg-primary-500/10",
  "线索 3 条已实锤": "text-foreground-200 border-background-600 bg-background-800",
};

export default function Mystery() {
  return (
    <section id="mystery" className="relative overflow-hidden bg-background-900 py-20 md:py-28">
      <div className="pointer-events-none absolute -right-24 top-10 h-80 w-80 rounded-full bg-primary-500/10 blur-3xl"></div>
      <div className="pointer-events-none absolute -left-24 bottom-10 h-80 w-80 rounded-full bg-accent-500/10 blur-3xl"></div>

      <div className="relative mx-auto max-w-[1280px] px-4 md:px-6">
        <SectionHead
          kicker="未解之谜"
          title="还没答案的事，先放在台面上"
          desc="我们更在意的是「证据缺口」。每个谜题都会列出已有线索与待证环节，欢迎你在下方补齐一块拼图。"
        />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
          {/* 左：谜题列表 */}
          <div className="space-y-4 lg:col-span-3">
            {mysteries.map((m, idx) => {
              const tone = stateTone[m.state] ?? stateTone["待新证"];
              return (
                <div
                  key={m.id}
                  className="group flex cursor-pointer items-start gap-5 rounded-xl border border-background-800 bg-background-950/70 p-5 transition-colors hover:border-background-600 md:p-6"
                >
                  <span className="font-heading flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-background-800 text-lg font-bold text-foreground-300">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1">
                    <h3 className="font-heading text-base font-bold text-foreground-50 group-hover:text-primary-300 md:text-lg">
                      {m.title}
                    </h3>
                    <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
                      <span className="flex items-center gap-1.5">
                        <i className="ri-focus-2-line text-accent-400"></i>
                        {m.clueCount} 条已有线索
                      </span>
                      <span className={`rounded-full border px-2.5 py-0.5 ${tone}`}>{m.state}</span>
                    </div>
                  </div>
                  <i className="ri-more-2-fill mt-1 text-foreground-600 group-hover:text-foreground-300"></i>
                </div>
              );
            })}

            {/* 贡献线索卡 */}
            <a
              href="#cta"
              className="flex items-center justify-between gap-4 rounded-xl border border-dashed border-primary-500/40 bg-primary-500/5 p-5 transition-colors hover:bg-primary-500/10 md:p-6"
            >
              <div className="flex items-center gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-950">
                  <i className="ri-add-line text-2xl"></i>
                </span>
                <div>
                  <p className="font-heading text-base font-bold text-foreground-50">你手里有一块拼图？</p>
                  <p className="mt-1 text-sm text-foreground-400">匿名提供线索，编辑核实后归入对应档案。</p>
                </div>
              </div>
              <i className="ri-arrow-right-line text-primary-300"></i>
            </a>
          </div>

          {/* 右：线索墙预览 */}
          <div className="rounded-2xl border border-background-800 bg-background-950/80 p-6 lg:col-span-2 lg:p-7">
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="ri-pushpin-2-fill text-accent-400"></i>
                <h3 className="font-heading text-lg font-bold text-foreground-50">本谜已有线索</h3>
              </div>
              <span className="rounded-full bg-background-800 px-2.5 py-1 text-[11px] text-foreground-400">待证</span>
            </div>

            <ol className="relative space-y-7 border-l border-background-800 pl-6">
              {mysteryClues.map((c, idx) => (
                <li key={c.id} className="relative">
                  <span className="absolute -left-[31px] top-1 h-3 w-3 rounded-full border-2 border-background-800 bg-accent-400"></span>
                  <p className="mb-1 text-xs font-medium text-accent-300">{c.who}</p>
                  <p className="text-sm leading-relaxed text-foreground-300">{c.text}</p>
                </li>
              ))}
            </ol>

            <div className="mt-8 rounded-lg border border-background-800 bg-background-900 p-4 text-sm text-foreground-400">
              <span className="mb-1 flex items-center gap-1.5 text-xs text-foreground-500">
                <i className="ri-information-line text-accent-400"></i> 线索缺口提示
              </span>
              线索 A 与 B 目前仍缺少一条可相互验证的公开记录，我们正在缩小范围。
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}